/**
 * @file src/input/sensorSwitches.js
 * @description The device-sensor SWITCHES: one per deck per dimension, whether or not a room exists.
 * Same no-window-globals module-singleton pattern as the panel and deck bridges.
 *
 * ── Why the switch cannot live on the sensor controller ──
 *
 * There is ONE `SensorController` per client, because there is one phone in one hand. It follows
 * whichever deck the player is focused on. That is right for the DEVICE and wrong for the SWITCH:
 * every deck has its own switch, and a switch held by the one controller is a switch shared by every
 * deck — press X on deck II and X lit on deck I, III and IV as well, because there was only ever one
 * X to light. The cosmic LFOs never had this problem: each voice builds its own three, so each tile
 * asks its own. The sensor enable gets the same shape here, without needing a controller per voice.
 *
 * So the two are separated:
 *
 *  - the SWITCH is per deck and lives in this module. It survives a tile unmounting, answers for a
 *    deck nobody is looking at, and needs no controller to exist. It is the ONLY place the enable is
 *    stored; the controller keeps no copy.
 *  - the DEVICE is this client's own, and only the controller can work it. It is asked to start or
 *    stop only for the deck this player is focused on, because that is the only deck it can reach.
 *
 * ── Who drives ──
 *
 * Whoever presses it ON drives it with their own device; in a room a peer's ON stands this device
 * down and leaves the switch lit. That single rule settles the switch and the driver together, so a
 * handover is press-off-press-on and two simultaneous presses cannot leave two phones on one axis.
 * Discrete, last-write-wins, no lease and no lock — a lease is a hand on a knob, and nobody's hand
 * rests on a button. With no room there is nobody else, so every lit switch is this client's.
 *
 * ── Who is driving, on screen ──
 *
 * "On" alone is ambiguous with two people: it says the axis is live, not whose hand is on it. So
 * every switch also carries the id of the client whose press turned it on, and the toggle draws
 * that player's avatar. It comes free — a press already arrives stamped with its sender.
 *
 * Directions across this seam:
 *  - `pressSensor` (voice UI → store): a local press — apply, drive, and in a room publish.
 *  - `applyRemoteSensor` (wire → store): a peer's press — apply, stand this device down.
 *  - `applySensorSnapshot` (wire → store): how things ARE, which never changes who drives.
 *
 * The store announces every change itself — to the deck's own toggles through `subscribeVoiceSensors`
 * and to the document as `sensorToggleChanged`, which is what the paired phone is mirrored from. The
 * controller announces nothing: a listener that re-reads on the event must find the switch already
 * written, and one announcer is the only way to guarantee that.
 */

import { voiceRegistry, PRIMARY_VOICE_ID } from '../voice/VoiceRegistry.js';
import { resolveScopedContext } from './source/ScopingResolver.ts';

/** The axes carrying a device-sensor toggle. */
const AXES = Object.freeze(['x', 'y', 'z']);

/** The dimension key for a deck that has no active dimension (a single-dimension orbiter). */
const NO_DIMENSION = 'default';

/**
 * The decks a paired phone drives on its own, by the phone's own selection. The phone reaches them
 * without the focus: its frames are written to each of them directly. Owned here, not on the
 * controller, because "can this client reach that deck" is a question the switches answer.
 */
let externalTargets = new Set();

/** Replace the set of decks the paired phone drives. Empty means it drives nothing. */
export function setExternalTargets(ids) {
  externalTargets = new Set(Array.isArray(ids) ? ids.filter((id) => typeof id === 'string' && id) : []);
}

/** The decks the paired phone drives, in the order it picked them. */
export function externalTargetIds() {
  return [...externalTargets];
}

/**
 * Can this client's own hardware reach `voiceId`? Its one controller follows the focused deck, and
 * its paired phone reaches whatever the phone selected.
 */
function reaches(voiceId) {
  if (externalTargets.has(voiceId)) return true;
  const focused = focusedVoiceId?.();
  return focused == null || focused === voiceId;
}

/** The document event a switch change is announced on. The paired phone mirrors from it. */
export const SENSOR_CHANGED_EVENT = 'sensorToggleChanged';

/**
 * `voiceId::dimensionId` → { x, y, z } where each axis is `{ on, by }` — the switch for that deck ON
 * THAT DIMENSION, and the client whose press turned it on. The one truth. `by` is null for an axis
 * nobody has claimed (always, outside a room), and survives its claimant leaving: the switch stays
 * on until somebody presses it.
 *
 * Per DIMENSION because everything else about an axis is: the sensor controller starts and stops
 * the device per dimension and re-reads the switches on every dimension change. A single switch per
 * deck drifted the moment the dimension moved — the switch stayed lit on a dimension nothing was
 * driving, or the controller resumed an axis from an earlier session while the switch read off.
 */
const switches = new Map();
/** `voiceId::dimensionId` → Set(axis) — the axes THIS client claimed by pressing, in a room. */
const driven = new Map();
/** runtime voiceId → Set(listener) — React toggles re-reading their own deck. */
const listeners = new Map();

// ── Realm seams: how the store reaches the focused deck and this client's device ──────────────
// Defaults read the voice registry, which is what the solo app has. A room may override them (it
// builds the controller on demand for a player who never opened the Sensors panel).

/** This client's one sensor controller, attached by the controller itself when it is built. */
let attachedDevice = null;

/** Attach (or detach, with null) the controller that works this client's device. */
export function attachSensorDevice(controller) {
  attachedDevice = controller ?? null;
}

const REALM = Object.freeze({
  focusedVoice: () => voiceRegistry.activeId ?? null,
  dimensionOf: (voiceId) => resolveScopedContext({
    modeController: voiceRegistry.get(voiceId)?.worldMode ?? null,
  }).dimensionId ?? NO_DIMENSION,
  // Pressing a deck's toggle means driving that deck, so the press focuses it first — the pairing
  // button does the same thing for the same reason. There is one controller and it follows the
  // focused voice; without this a press on an unfocused tile lit a switch with nobody behind it.
  // Not when the deck is already in the selection: the multi-focus gang reaches it (gated by this
  // very switch), and a plain focus would collapse the selection the player built.
  focus: (voiceId) => {
    if (voiceRegistry.has(voiceId) && !voiceRegistry.isSelected(voiceId)) voiceRegistry.setActive(voiceId);
  },
  device: () => attachedDevice,
});

let focusedVoiceId = REALM.focusedVoice; // () => string|null — the deck this client's device reaches
let activeDimension = REALM.dimensionOf; // (voiceId) => string — which dimension that deck is playing
let focusVoice = REALM.focus; // (voiceId) => void — put this client's device ON that deck
let resolveDevice = REALM.device; // ({ create }) => SensorController|null

// ── Wire seams: only a room has them. All null solo, which is what `isRoomActive` reads. ─────────
let publishSensor = null; // (voiceId, axis, on) => void
let selfClientId = null; // () => string|null — who a press by THIS client is by
let canEditVoice = null; // (voiceId) => boolean

/** Is a room wired up? False solo: the switches still work, there is just nobody to tell. */
export function isRoomActive() {
  return publishSensor !== null;
}

/**
 * Room client → this seam. Passing nothing (room over) drops the wire and restores the realm
 * defaults. The switches are NOT cleared: they are this client's decks, and whatever was lit for
 * them is still lit once the room is gone.
 */
export function setRoomSensorPublishers({
  publish = null,
  canEdit = null,
  focusedVoice = null,
  device = null,
  selfId = null,
  dimensionOf = null,
  focus = null,
} = {}) {
  publishSensor = typeof publish === 'function' ? publish : null;
  canEditVoice = typeof canEdit === 'function' ? canEdit : null;
  selfClientId = typeof selfId === 'function' ? selfId : null;
  focusedVoiceId = typeof focusedVoice === 'function' ? focusedVoice : REALM.focusedVoice;
  resolveDevice = typeof device === 'function' ? device : REALM.device;
  activeDimension = typeof dimensionOf === 'function' ? dimensionOf : REALM.dimensionOf;
  focusVoice = typeof focus === 'function' ? focus : REALM.focus;
  if (!publishSensor) driven.clear(); // solo, every lit switch is this client's — see isDrivenLocally
  // BOTH ways. A toggle mounted before the room existed may now be drawing a driver it did not
  // have; leaving one, the faces go. Nothing else would tell it.
  for (const voiceId of [...listeners.keys()]) notify(voiceId);
}

/** Subscribe a voice's UI to ITS OWN deck's switch. Returns an unsubscribe. */
export function subscribeVoiceSensors(voiceId, listener) {
  if (!voiceId || typeof listener !== 'function') return () => {};
  if (!listeners.has(voiceId)) listeners.set(voiceId, new Set());
  listeners.get(voiceId).add(listener);
  return () => {
    const set = listeners.get(voiceId);
    if (!set) return;
    set.delete(listener);
    // Every card deal mints a fresh voice id, so an empty set left behind is a slow leak.
    if (set.size === 0) listeners.delete(voiceId);
  };
}

function notify(voiceId) {
  const set = listeners.get(voiceId);
  if (!set) return;
  for (const listener of set) {
    try {
      listener();
    } catch (error) {
      console.warn('[sensorSwitches] sensor listener failed', error);
    }
  }
}

/**
 * Tell everyone a switch moved: the deck's own toggles, and the document — which is where the
 * paired phone's mirror and the single-orbiter chrome listen. Fired AFTER the write, always.
 */
function announce(voiceId, dimensionId, axis, on, source) {
  notify(voiceId);
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent(SENSOR_CHANGED_EVENT, {
    detail: {
      axis,
      key: `s${axis.toUpperCase()}`,
      value: on === true,
      source,
      dimension: dimensionId,
      voiceId,
    },
  }));
}

/** The voice a caller with no voice of its own means: the focused one, else the single orbiter. */
export function defaultSensorVoiceId() {
  return focusedVoiceId?.() ?? PRIMARY_VOICE_ID;
}

/** The switch key: a deck ON a dimension — the one it is playing unless the caller names another. */
function deckKey(voiceId, dimensionId = null) {
  return `${voiceId}::${dimensionId ?? activeDimension?.(voiceId) ?? NO_DIMENSION}`;
}

/** Is `axis` switched on for `voiceId` on the dimension it is playing? The lamp every tile reads. */
export function isSensorEnabled(voiceId, axis) {
  if (!AXES.includes(axis)) return false;
  return switches.get(deckKey(voiceId))?.[axis]?.on === true;
}

/** Is `axis` switched on for `voiceId` on `dimensionId` specifically? For routing and snapshots. */
export function isSensorEnabledOn(voiceId, dimensionId, axis) {
  if (!AXES.includes(axis)) return false;
  return switches.get(deckKey(voiceId, dimensionId))?.[axis]?.on === true;
}

/**
 * Every dimension of `voiceId` whose `axis` switch is on. What the controller routes an axis to:
 * an axis drives exactly the dimensions that switched it on, not the one you happen to be on.
 */
export function enabledDimensions(voiceId, axis) {
  const out = [];
  if (!voiceId || !AXES.includes(axis)) return out;
  const prefix = `${voiceId}::`;
  for (const [key, state] of switches) {
    if (key.startsWith(prefix) && state[axis]?.on === true) out.push(key.slice(prefix.length));
  }
  return out;
}

/**
 * Every dimension of `voiceId` on which THIS client's device is the one driving `axis` — what the
 * controller routes a sample to. A lit switch a peer claimed is theirs to feed, not ours: routing
 * from the lamp alone kept this phone writing an axis the badge said belonged to somebody else.
 * Solo there is nobody else, so this is every lit switch. Nothing while the deck is not focused.
 */
export function drivenDimensions(voiceId, axis) {
  if (!voiceId || !AXES.includes(axis) || !reaches(voiceId)) return [];
  if (!publishSensor) return enabledDimensions(voiceId, axis);
  const out = [];
  const prefix = `${voiceId}::`;
  for (const [key, axes] of driven) {
    if (key.startsWith(prefix) && axes.has(axis)) out.push(key.slice(prefix.length));
  }
  return out;
}

/** Is this client's device driving anything at all on `voiceId`? The device's reason to run. */
export function anySensorDriven(voiceId) {
  return AXES.some((axis) => drivenDimensions(voiceId, axis).length > 0);
}

/**
 * The client driving `axis` on `voiceId`, or null when it is off (or was switched on by somebody
 * the room can no longer name). What the toggle draws an avatar for. Always null solo.
 */
export function sensorDriver(voiceId, axis) {
  if (!AXES.includes(axis)) return null;
  const state = switches.get(deckKey(voiceId))?.[axis];
  return state?.on === true ? (state.by ?? null) : null;
}

/** The whole deck's switch, or null when nothing has ever been said about it. For the snapshot. */
export function voiceSensorSnapshot(voiceId) {
  const state = switches.get(deckKey(voiceId));
  if (!state) return null;
  const out = {};
  for (const axis of AXES) {
    out[axis] = { on: state[axis]?.on === true, by: state[axis]?.by ?? null };
  }
  return out;
}

function blankSwitch() {
  return { x: { on: false, by: null }, y: { on: false, by: null }, z: { on: false, by: null } };
}

function writeSwitch(voiceId, dimensionId, axis, on, by) {
  const key = deckKey(voiceId, dimensionId);
  const state = switches.get(key) ?? blankSwitch();
  // Switching it OFF clears the claim with it — nobody is driving an axis that is off, and a stale
  // face on a dark toggle would be a small lie waiting for the next person to read it.
  state[axis] = { on: on === true, by: on === true ? (by ?? null) : null };
  switches.set(key, state);
}

/**
 * Is this client driving `axis` on `voiceId` with its own device?
 *
 * Focus is part of the answer, not a separate check: there is one controller and it follows the
 * focused voice, so the moment you look at another deck your device is no longer on this one. A
 * claim that outlived its focus would keep this client's face on a switch nothing was driving, and
 * would make the snapshot skip that axis for good.
 *
 * Solo there is nobody else to have claimed it: every lit switch on the focused deck is this
 * client's to drive, so no separate claim is kept.
 */
export function isDrivenLocally(voiceId, axis) {
  if (!reaches(voiceId)) return false;
  if (!publishSensor) return isSensorEnabled(voiceId, axis);
  return driven.get(deckKey(voiceId))?.has(axis) === true;
}

function setDriven(voiceId, dimensionId, axis, on) {
  const key = deckKey(voiceId, dimensionId);
  if (on) {
    if (!driven.has(key)) driven.set(key, new Set());
    driven.get(key).add(axis);
  } else {
    driven.get(key)?.delete(axis);
  }
}

/**
 * Ask this client's own device to start or stop on an axis — only ever for the deck it is focused
 * on, because a single controller follows the focused voice and cannot reach any other deck.
 *
 * `create` is false for a stand-down: a client that never opened the Sensors panel has no
 * controller and therefore nothing running, and building one to switch off something that was
 * never on would be work for nothing.
 */
function driveLocalDevice(voiceId, axis, on) {
  const focused = focusedVoiceId?.();
  if (focused != null && focused !== voiceId) return false;
  const device = resolveDevice?.({ create: on === true }) ?? null;
  if (!device) return false;
  // A no-op write is not free: the controller re-derives its listeners on every write. Only ever
  // say something when the device disagrees.
  if (device.isAxisEnabled?.(axis) === (on === true)) return on === true;
  // The controller's own listener lifecycle does the real work; switching on can still ask for the
  // motion permission — the player pressed it, so that prompt is theirs.
  device.setAxisActive?.(axis, on === true);
  return true;
}

/**
 * Voice UI → store: this player pressed the switch on `voiceId`, on the dimension it is playing.
 * The wire carries no dimension (the active dimension is itself routed, so every client is on the
 * same one when a press lands), which is why a press can only ever mean the playing dimension.
 *
 * Returns false when the room refuses (this deck is not theirs to drive), so the caller can leave
 * the switch where it was rather than showing a press that did not happen.
 */
export function pressSensor(voiceId, axis, on, { source = 'local' } = {}) {
  if (!voiceId || !AXES.includes(axis)) return false;
  if (canEditVoice && canEditVoice(voiceId) === false) {
    notify(voiceId); // correct the optimistic React toggle
    return false;
  }
  const playing = activeDimension?.(voiceId) ?? NO_DIMENSION;
  // Pressing a deck's sensor toggle means driving THAT deck, so put the device on it first — unless
  // something of this client's already reaches it: the paired phone, by the phone's own selection,
  // never moves the screen's focus.
  if (on === true && !reaches(voiceId)) focusVoice?.(voiceId);
  // The switch is written BEFORE the device is worked: the device decides whether to keep listening
  // by asking whether anything on its deck is still driven, so it has to find this one already off.
  writeSwitch(voiceId, playing, axis, on, null);
  // Driving now: this client's device took the axis, or its paired phone reaches the deck and will
  // feed it directly. Either way the claim is this client's, and the room must hear it as such.
  const deviceTook = on === true && driveLocalDevice(voiceId, axis, true);
  if (!deviceTook) driveLocalDevice(voiceId, axis, false);
  const drivingNow = deviceTook || (on === true && externalTargets.has(voiceId));
  setDriven(voiceId, playing, axis, drivingNow);
  // Stamp the driver ONLY when this device really took the axis. The badge is documented as "whose
  // device is on this axis"; naming yourself over an axis nothing is driving makes it say something
  // untrue, and there is no way to tell from the outside.
  if (drivingNow) writeSwitch(voiceId, playing, axis, true, selfClientId?.() ?? null);
  announce(voiceId, playing, axis, on === true, source);
  publishSensor?.(voiceId, axis, on === true);
  return true;
}

/**
 * Wire → store: a peer pressed the switch. The lamp follows them, and because they just claimed
 * the axis with their own device, this one comes off it.
 */
export function applyRemoteSensor(voiceId, axis, on, byClientId = null) {
  if (!voiceId || !AXES.includes(axis)) return;
  const wasMine = isDrivenLocally(voiceId, axis);
  const playing = activeDimension?.(voiceId) ?? NO_DIMENSION;
  writeSwitch(voiceId, playing, axis, on, byClientId);
  // Off, or somebody else's on — either way this device is no longer the one on this axis.
  if (wasMine) driveLocalDevice(voiceId, axis, false);
  setDriven(voiceId, playing, axis, false);
  // `room`, not `remote`: the paired phone's mirror drops `remote` (its own presses coming back) and
  // must still hear a peer's, or its lamp stays where the peer left it.
  announce(voiceId, playing, axis, on === true, 'room');
}

/**
 * Wire → store: a deck's settled switch, from a `player:state` snapshot.
 *
 * A snapshot describes how things ARE; a press is somebody claiming an axis. Only the second may
 * change hands — every co-owner of a deck publishes a snapshot built from its own view, so one that
 * merely missed a press would otherwise take this player's phone off an axis nobody released.
 */
export function applySensorSnapshot(voiceId, sensors) {
  if (!voiceId || !sensors || typeof sensors !== 'object') return;
  const playing = activeDimension?.(voiceId) ?? NO_DIMENSION;
  let changed = false;
  for (const axis of AXES) {
    const entry = sensors[axis];
    const want = typeof entry === 'object' && entry !== null ? entry.on : entry;
    if (typeof want !== 'boolean') continue;
    if (isDrivenLocally(voiceId, axis)) continue;
    // The snapshot names the driver too, so a late joiner sees the same face as everybody else.
    writeSwitch(voiceId, playing, axis, want, typeof entry === 'object' ? entry.by ?? null : null);
    changed = true;
  }
  if (changed) notify(voiceId);
}

/**
 * Drop a deck's switches when its voice goes.
 *
 * Deliberately does NOT try to stop the device: by the time this runs the voice is already out of
 * the registry and the focus has moved, so there is no deck for the controller to be stopped ON.
 * The controller re-reads the newly focused voice's own switches on that focus change.
 */
export function forgetVoiceSensorState(voiceId) {
  // Every dimension of that deck, not just the one it was last on.
  for (const key of [...switches.keys()]) {
    if (key.startsWith(`${voiceId}::`)) switches.delete(key);
  }
  for (const key of [...driven.keys()]) {
    if (key.startsWith(`${voiceId}::`)) driven.delete(key);
  }
  notify(voiceId);
  listeners.delete(voiceId); // after the notify, so the toggle hears its deck go
}

// Voices come and go with every card deal. Forget the switches of a voice that was registered and
// no longer is — only one that WAS registered, so a switch written for a deck the wire announced
// before the registry dealt it is not thrown away by an unrelated deal.
const seenRegistered = new Set();
voiceRegistry.onVoicesChange?.(() => {
  const present = new Set(voiceRegistry.all().map((voice) => voice.id));
  for (const voiceId of seenRegistered) {
    if (!present.has(voiceId)) {
      seenRegistered.delete(voiceId);
      forgetVoiceSensorState(voiceId);
    }
  }
  for (const voiceId of present) seenRegistered.add(voiceId);
});

/**
 * Make this client's device agree with the switch for the deck it is on, on the dimension it is NOW
 * on. Called whenever the DIMENSION moves or the FOCUS moves — the two events on which "which axes
 * is my device feeding" has a new answer.
 *
 * Both are needed, and focus is the one that bites hardest: the device only ever ran for the deck it
 * was on, so refocusing a deck whose X is lit must start it, and leaving one must stop it. In a room
 * a peer switching an axis off on a deck you are not looking at cannot reach your controller either;
 * this is where that catches up.
 *
 * It converges BOTH ways. Starting here cannot surprise anyone with a permission prompt, because it
 * only ever resumes an axis this client already claimed by pressing.
 */
export function syncDeviceToSwitch(voiceId) {
  if (!voiceId || focusedVoiceId?.() !== voiceId) return;
  for (const axis of AXES) {
    driveLocalDevice(voiceId, axis, isSensorEnabled(voiceId, axis) && isDrivenLocally(voiceId, axis));
  }
  notify(voiceId);
}

/** Test seam: forget everything, as if nothing had ever been pressed and no room had ever run. */
export function resetSensorBridge() {
  switches.clear();
  driven.clear();
  listeners.clear();
  seenRegistered.clear();
  externalTargets = new Set();
  attachedDevice = null;
  setRoomSensorPublishers();
}
